import depsloader from "../../../depsloader";

const REQUEST_TIMEOUT = 20000;
const DISTRICT_TYPES = ["district", "locality"];

function runtimeConfig() {
  const config = (typeof window !== "undefined" && window.ScreenworkGeocoder) || {};

  return {
    searchUrl: config.searchUrl || "https://api.screenwork.de/geocoder/search",
    reverseUrl: config.reverseUrl || "https://api.screenwork.de/geocoder/reverse",
    key: typeof config.key === "string" ? config.key.trim() : ""
  };
}

function isFiniteNumber(value) {
  return typeof value === "number" && isFinite(value);
}

function validCoordinates(lat, lng) {
  return isFiniteNumber(lat) && isFiniteNumber(lng)
    && lat >= -90 && lat <= 90
    && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (p.length === 0 && g.name) {
      p.push(g.name);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Antworten ohne properties.type (alter Geocoder)
  _legacyAddress(p) {
    const a = {};
    let name = p.name;
    let street = p.street;

    if (p.osm_key == "highway") {
      street = p.name;
      name = null;
    }

    if (street) {
      a.street = street;
      if (p.housenumber) {
        a.number = p.housenumber;
      }
    }

    if (p.postcode && p.city && name) {
      a.zip = p.postcode;
      a.city = p.city;
      if (p.city != name) {
        const district = p.osm_key == "boundary"
          || (p.osm_key == "place" && p.osm_value == "suburb");
        if (district) {
          a.district = name;
        }
      }
    } else if (p.postcode && p.city) {
      a.zip = p.postcode;
      a.city = p.city;
    } else if (p.postcode && name) {
      a.zip = p.postcode;
      a.city = name;
    } else if (p.city) {
      a.city = p.city;
    } else if (name) {
      a.name = name;
    }

    return a;
  }

  _typedAddress(p) {
    const a = {};
    const type = p.type;

    if (type === "street") {
      a.street = p.street || p.name;
    } else if (p.street) {
      a.street = p.street;
      if (p.housenumber) {
        a.number = p.housenumber;
      }
    }

    if (type === "city") {
      a.city = p.name || p.city;
    } else if (p.city) {
      a.city = p.city;
    }

    if (DISTRICT_TYPES.indexOf(type) !== -1 && p.name && p.name !== a.city) {
      a.district = p.name;
    } else if (p.district && p.district !== a.city) {
      a.district = p.district;
    }

    if (p.postcode) {
      a.zip = p.postcode;
    }

    if (!a.street && !a.city && !a.district && p.name) {
      a.name = p.name;
    }

    return a;
  }

  _geoaddress(feature) {
    if (!feature || !feature.geometry || !Array.isArray(feature.geometry.coordinates)) {
      return null;
    }

    const lng = feature.geometry.coordinates[0];
    const lat = feature.geometry.coordinates[1];
    if (!validCoordinates(lat, lng)) {
      return null;
    }

    const p = feature.properties || {};
    const geoaddress = p.type ? this._typedAddress(p) : this._legacyAddress(p);

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);
    delete geoaddress.name;

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    if (res && res.data && Array.isArray(res.data.features)) {
      return res.data.features;
    }

    return [];
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const config = runtimeConfig();
    if (!config.key || !q) {
      return Promise.resolve([]);
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", config.key);

    return this.axios.get(config.searchUrl, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const results = [];

      this._features(res).forEach(feature => {
        const geoaddress = this._geoaddress(feature);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    const config = runtimeConfig();
    lat = Number(lat);
    lng = Number(lng);

    if (!config.key) {
      return Promise.reject(new Error("geocoder key missing"));
    }

    if (!validCoordinates(lat, lng)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const params = new URLSearchParams();
    params.append("lat", lat);
    params.append("lon", lng);
    params.append("lang", "de");
    params.append("key", config.key);

    return this.axios.get(config.reverseUrl, {
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const features = this._features(res);
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;

      if (!geoaddress) {
        throw new Error("invalid data");
      }

      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
